import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { axiosPrivate } from 'modules/networkTools/networkTools';
import { NoteFormValues } from 'modules/notes/notePopup/noteEdit/utils';
import { getTimezoneOffsetHours } from 'utils/dateHelpers/dateHelpers';

const getChunkExtension = (mimeType: string) => {
  if (mimeType.includes('webm')) return 'webm';
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('wav')) return 'wav';
  if (mimeType.includes('mp4')) return 'mp4';
  return 'bin';
};

const getErrorMessage = (error: any | AxiosError) => {
  if (error.response && error.response.data.errors) {
    return error.response.data.errors;
  }

  return error.message;
};

export const initializeRecordingSession = createAsyncThunk(
  'aiScribe/initializeRecordingSession',
  async (
    {
      clientId,
      noteType,
      note
    }: {
      clientId: number | null;
      noteType: string;
      note?: NoteFormValues | null;
    },
    { rejectWithValue }
  ) => {
    const timezoneOffset = getTimezoneOffsetHours(
      note?.dateOfEncounter,
      note?.startTimeOfEncounter
    );
    try {
      const response = await axiosPrivate.put(
        `/ai-scribe/${clientId}/initialize`,
        {
          noteType: noteType === '' ? null : noteType,
          note
        },
        {
          headers: {
            'x-time-zone-offset': timezoneOffset
          }
        }
      );

      return response.data;
    } catch (error: any | AxiosError) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const uploadRecordingChunk = createAsyncThunk(
  'aiScribe/uploadRecordingChunk',
  async (
    {
      clientId,
      recordingSessionId,
      chunk,
      ordinalNumber,
      duration
    }: {
      clientId: number | null;
      recordingSessionId: string;
      chunk: Blob;
      ordinalNumber: number;
      duration: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const formData = new FormData();
      const fileExtension = getChunkExtension(chunk.type);
      formData.append('chunk', chunk, `${ordinalNumber}.${fileExtension}`);

      const searchParams = new URLSearchParams({
        ordinalNumber: ordinalNumber.toString(),
        fileUuid: recordingSessionId.toString(),
        clientId: clientId?.toString() ?? '',
        duration: duration.toString()
      });

      const response = await axiosPrivate.post(
        `/ai-scribe/${clientId}/upload?${searchParams.toString()}`,
        formData,
        {
          'axios-retry': {
            retries: 4
          }
        }
      );

      return response.data;
    } catch (error: any | AxiosError) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const startRecordingTranscription = createAsyncThunk(
  'aiScribe/startRecordingTranscription',
  async (
    {
      clientId,
      recordingSessionId
    }: {
      clientId: number | null;
      recordingSessionId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const searchParams = new URLSearchParams({
        fileUuid: recordingSessionId,
        clientId: clientId?.toString() ?? ''
      });

      const response = await axiosPrivate.post(
        `/ai-scribe/${clientId}/transcribe?${searchParams.toString()}`
      );

      return response.data;
    } catch (error: any | AxiosError) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getCurrentAIScribe = createAsyncThunk(
  'aiScribe/getCurrentAIScribe',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get('ai-scribe/current');
      return response.data;
    } catch (error: any | AxiosError) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);
